import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCheck, 
  FiChevronLeft, 
  FiChevronRight, 
  FiPackage, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin,
  FiMessageSquare 
} from 'react-icons/fi';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import toast from 'react-hot-toast';

/**
 * QuoteModal Component - Gough Kelly Style
 * Multi-step quote request wizard
 */

const QuoteModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    serviceType: '',
    products: [],
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    message: '',
  });

  const steps = [
    { number: 1, title: 'Service Type' },
    { number: 2, title: 'Your Details' },
    { number: 3, title: 'Additional Info' },
    { number: 4, title: 'Confirmation' },
  ];

  const serviceTypes = [
    { 
      id: 'installation', 
      title: 'New Installation', 
      description: 'Complete security system installation for new premises',
      icon: '🏗️'
    },
    { 
      id: 'upgrade', 
      title: 'System Upgrade', 
      description: 'Upgrade existing security systems with latest technology',
      icon: '⚡'
    },
    { 
      id: 'maintenance', 
      title: 'Maintenance Contract', 
      description: 'Regular maintenance and support for existing systems',
      icon: '🔧'
    },
    { 
      id: 'consultation', 
      title: 'Security Consultation', 
      description: 'Professional security assessment and recommendations',
      icon: '💡'
    },
  ];

  const productCategories = [
    { id: 'cameras', label: 'CCTV Systems' },
    { id: 'alarms', label: 'Alarm Systems' },
    { id: 'access', label: 'Access Control' },
    { id: 'fire', label: 'Fire Safety' },
    { id: 'gates', label: 'Gates & Barriers' },
    { id: 'monitoring', label: '24/7 Monitoring' },
  ];

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(p => p !== productId)
        : [...prev.products, productId]
    }));
  };

  const validateStep = () => {
    if (currentStep === 1 && !formData.serviceType) {
      toast.error('Please select a service type');
      return false;
    }
    if (currentStep === 2) {
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error('Please fill in all required fields');
        return false;
      }
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep === 3) {
        // Submit quote
        handleSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // API call would go here
    console.log('Quote Request:', formData);
    toast.success('Quote request submitted successfully!');
    setCurrentStep(4);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      serviceType: '',
      products: [],
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      postcode: '',
      message: '',
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Request a Quote" size="lg">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors duration-300 ${
                  currentStep >= step.number
                    ? 'bg-accent-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step.number ? <FiCheck /> : step.number}
              </div>
              <span className="text-xs mt-2 text-center text-gray-600 font-medium">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`h-1 flex-1 transition-colors duration-300 ${
                  currentStep > step.number ? 'bg-accent-500' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="min-h-[400px]"
        >
          {/* Step 1: Service Type */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4 text-primary-800">What service do you need?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {serviceTypes.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setFormData({ ...formData, serviceType: service.id })}
                    className={`p-6 border-2 rounded-lg text-left transition-all duration-300 hover:shadow-lg ${
                      formData.serviceType === service.id
                        ? 'border-accent-500 bg-accent-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-4xl mb-3">{service.icon}</div>
                    <h4 className="font-semibold text-gray-900 mb-2">{service.title}</h4>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <h4 className="font-semibold mb-3 text-gray-700">Products/Systems (Optional)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {productCategories.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductToggle(product.id)}
                      className={`p-3 border rounded-lg text-sm transition-all duration-300 ${
                        formData.products.includes(product.id)
                          ? 'border-accent-500 bg-accent-50 text-accent-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {product.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact Details */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <h3 className="text-lg font-semibold mb-4 text-primary-800">Your Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label flex items-center space-x-2">
                    <FiUser className="text-gray-500" />
                    <span>Full Name *</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="label flex items-center space-x-2">
                    <FiPackage className="text-gray-500" />
                    <span>Company Name</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="input"
                    placeholder="Your Company Ltd"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label flex items-center space-x-2">
                    <FiMail className="text-gray-500" />
                    <span>Email Address *</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="label flex items-center space-x-2">
                    <FiPhone className="text-gray-500" />
                    <span>Phone Number *</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                    placeholder="+44 20 1234 5678"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label flex items-center space-x-2">
                  <FiMapPin className="text-gray-500" />
                  <span>Property Address</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input"
                  placeholder="123 Main Street, London"
                />
              </div>

              <div>
                <label className="label">Postcode</label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                  className="input"
                  placeholder="SW1A 1AA"
                />
              </div>
            </div>
          )}

          {/* Step 3: Additional Information */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <h3 className="text-lg font-semibold mb-4 text-primary-800">Additional Details</h3>
              
              <div>
                <label className="label flex items-center space-x-2">
                  <FiMessageSquare className="text-gray-500" />
                  <span>Tell us about your requirements</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="input min-h-[150px]"
                  placeholder="Please provide any additional details about your security requirements, property size, specific concerns, or preferred timeline..."
                />
              </div>

              <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                <h4 className="font-semibold text-primary-800 mb-2">What happens next?</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start space-x-2">
                    <span className="text-accent-500 mt-1">✓</span>
                    <span>We'll review your requirements within 24 hours</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-accent-500 mt-1">✓</span>
                    <span>A security expert will contact you to discuss your needs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-accent-500 mt-1">✓</span>
                    <span>We'll arrange a site visit if needed (free of charge)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-accent-500 mt-1">✓</span>
                    <span>You'll receive a detailed quote with no obligation</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 4 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiCheck className="text-4xl text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Quote Request Received!</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Thank you for your interest. We've received your quote request and will get back to you within 24 hours.
              </p>
              <div className="bg-accent-50 p-4 rounded-lg mb-6 max-w-md mx-auto">
                <p className="text-sm text-gray-700">
                  A confirmation email has been sent to <strong>{formData.email}</strong>
                </p>
              </div>
              <Button text="Close" type="primary" onClick={handleClose} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      {currentStep < 4 && (
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            text="Back"
            type="secondary"
            onClick={handleBack}
            disabled={currentStep === 1}
            icon={<FiChevronLeft />}
          />
          <Button
            text={currentStep === 3 ? 'Submit Request' : 'Next'}
            type="primary"
            onClick={handleNext}
            icon={currentStep < 3 ? <FiChevronRight /> : null}
          />
        </div>
      )}
    </Modal>
  );
};

export default QuoteModal;

